import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { chatWithAI } from '../services/geminiService';
import { buildMagnusContext } from '../services/magnusContext';
import { handleBasicModeQuery, getQuickAction } from '../services/magnusBasic';
import { PAGE_CONTEXT, MAGNUS_KNOWLEDGE } from '../constants/assistantContext';
import {
    isItemInProject,
    analyzeCurrentPageData,
    getSpecificRecommendations,
    findProblemsOnPage,
    lookupItem,
    findHighestDetectionNew,
    findHighestSeverity,
    findHighestOccurrence,
    findHighestRiskNew,
    answerTableQuestion,
    findControlsFor
} from '../services/assistantHelpers';

import { Message } from '../types/assistantTypes';

export const useAssistantLogic = () => {
    const location = useLocation();
    const { navigate: contextNavigate, allData, activeProject } = useAppContext();
    const currentPageContext = PAGE_CONTEXT[location.pathname];

    // State
    const [isOpen, setIsOpen] = useState(false);
    const [showIntroBubble, setShowIntroBubble] = useState(true);
    const [isPrimeMode, setIsPrimeMode] = useState(false);
    const [isThinking, setIsThinking] = useState(false);
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState('');

    // Initial Greeting
    useEffect(() => {
        const greeting = PAGE_CONTEXT[location.pathname]
            ? `Hey! You're on ${PAGE_CONTEXT[location.pathname].name}. ${PAGE_CONTEXT[location.pathname].purpose} Need any help?`
            : MAGNUS_KNOWLEDGE.greetings[0];

        setMessages([{ role: 'magnus', content: greeting }]);
    }, [location.pathname]);

    // Rate Limit Check
    useEffect(() => {
        const checkRateLimit = () => {
            const storedLimit = localStorage.getItem('magnus_rate_limit');
            if (storedLimit) {
                const limitData = JSON.parse(storedLimit);
                const now = Date.now();
                if (limitData.until > now) {
                    setIsRateLimited(true);
                    setRateLimitMessage(`Token rate limit reached. Running in Basic Mode until ${new Date(limitData.until).toLocaleTimeString()}`);
                } else {
                    localStorage.removeItem('magnus_rate_limit');
                    setIsRateLimited(false);
                    setRateLimitMessage(null);
                }
            }
        };
        checkRateLimit();
    }, []);

    const magnusContext = useMemo(() =>
        buildMagnusContext(location, allData, activeProject),
        [location.pathname, allData, activeProject]
    );

    const handleClear = () => {
        const greeting = currentPageContext
            ? `Hey! You're on ${currentPageContext.name}. ${currentPageContext.purpose} Need any help?`
            : MAGNUS_KNOWLEDGE.greetings[0];
        setMessages([{ role: 'magnus', content: greeting }]);
    };

    // --- Answer Finding Logic ---
    const findAnswer = (query: string): { answer: string; action?: any } => {
        const lowerQuery = query.toLowerCase();

        // 1. Context Aware
        if (currentPageContext) {
            if (lowerQuery.includes('where am i') || lowerQuery.includes('what page')) {
                return {
                    answer: `You're on ${currentPageContext.name}. ${currentPageContext.purpose}\n\nRelated: ${currentPageContext.relatedPages.map(p => p.label).join(', ')}`,
                    action: { type: 'highlight', target: `a[href="${location.pathname}"]`, label: currentPageContext.name }
                };
            }
        }

        // 2. Risk Queries
        if (/highest|worst|biggest|top|bad/.test(lowerQuery)) {
            if (lowerQuery.includes('detect')) return { answer: findHighestDetectionNew(allData) };
            if (lowerQuery.includes('severe')) return { answer: findHighestSeverity(allData) };
            if (lowerQuery.includes('likely')) return { answer: findHighestOccurrence(allData) };
            if (lowerQuery.includes('risk') || lowerQuery.includes('rpn')) return { answer: findHighestRiskNew(allData) };
        }

        // 3. Data Analysis
        if (lowerQuery.includes('analyze') || lowerQuery.includes('check my data')) return { answer: analyzeCurrentPageData(allData, location, activeProject || 'all') };
        if (lowerQuery.includes('problems') || lowerQuery.includes('issues')) return { answer: findProblemsOnPage(allData, location) };
        if (lowerQuery.includes('recommend')) return { answer: getSpecificRecommendations(allData) };

        // 4. Common Questions
        const tableAns = answerTableQuestion(query, allData);
        if (tableAns) return { answer: tableAns };

        if (lowerQuery.includes('control') && lowerQuery.length > 15) {
            const ctrlAns = findControlsFor(query, allData);
            if (ctrlAns) return { answer: ctrlAns };
        }

        // 5. Item Lookup
        if (lowerQuery.includes('find') || lowerQuery.includes('show') || lowerQuery.includes('tell me about')) {
            const itemInfo = lookupItem(query, allData, location);
            if (itemInfo) return { answer: itemInfo };
        }

        // 6. Navigation (simplified)
        for (const [key, val] of Object.entries(MAGNUS_KNOWLEDGE.navigation)) {
            if (lowerQuery.includes(key.replace('-', ' '))) return val;
        }

        // 7. General Knowledge
        if (lowerQuery.includes('rpn')) return MAGNUS_KNOWLEDGE.fmeaHelp.rpn;
        // ... (other mapping if needed, simplified for brevity)

        return { answer: MAGNUS_KNOWLEDGE.fallback };
    };


    const handleSend = async () => {
        if (!inputValue.trim()) return;
        const userMsg: Message = { role: 'user', content: inputValue };
        setMessages(prev => [...prev, userMsg]);
        const currentInput = inputValue;
        setInputValue('');

        // Rate Limit Warning
        if (isRateLimited && isPrimeMode) {
            setMessages(prev => [...prev, { role: 'magnus', content: `⚠️ Rate Limit Active. Switching to Basic Mode.` }]);
        }

        // Prime Mode (LLM)
        if (isPrimeMode && !isRateLimited) {
            setIsThinking(true);
            try {
                // Filter items for context
                const allFmeas = allData?.fmeaItems || [];
                const projectFmeas = allFmeas.filter((f: any) => isItemInProject(activeProject || 'all', allData, f.projectName, f.epochId));

                // Simplified context building
                const context = {
                    pageName: currentPageContext?.name,
                    activeProject: activeProject || 'All Projects',
                    fmeaItems: projectFmeas.slice(0, 50), // Limit context size
                    botId: 'BOT-MAGNUS-01',
                    conversationHistory: messages
                };

                const response = await chatWithAI(currentInput, context);
                setMessages(prev => [...prev, { role: 'magnus', content: response }]);
            } catch (e) {
                setMessages(prev => [...prev, { role: 'magnus', content: "Brain freeze! (Connection error)" }]);
            } finally {
                setIsThinking(false);
            }
        } else {
            // Basic Mode
            setIsThinking(true);
            try {
                let result = getQuickAction(currentInput, magnusContext);
                if (!result) {
                    const ans = findAnswer(currentInput);
                    if (ans.answer !== MAGNUS_KNOWLEDGE.fallback) result = ans;
                }

                if (!result) {
                    // Fallback to basic AI helper if available, else standard fallback
                    result = await handleBasicModeQuery(currentInput, magnusContext, messages);
                }

                setMessages(prev => [...prev, { role: 'magnus', content: result.answer, action: result.action }]);
            } catch (e) {
                console.error(e);
                setMessages(prev => [...prev, { role: 'magnus', content: "Error processing request." }]);
            } finally {
                setIsThinking(false);
            }
        }
    };

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [highlightTarget, setHighlightTarget] = useState<string | null>(null);
    const [showWorkflowAnalysis, setShowWorkflowAnalysis] = useState(false);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isOpen]);

    // Handle Actions (Navigation / Highlight)
    const handleAction = (action: { type: 'highlight' | 'navigate', target: string }) => {
        if (action.type === 'highlight') {
            setHighlightTarget(action.target);
            setTimeout(() => setHighlightTarget(null), 5000);
        } else if (action.type === 'navigate') {
            contextNavigate(action.target);
        }
    };

    return {
        isOpen, setIsOpen,
        showIntroBubble, setShowIntroBubble,
        isPrimeMode, setIsPrimeMode,
        isThinking,
        isRateLimited,
        messages,
        inputValue, setInputValue,
        handleSend,
        handleClear,
        currentPageContext,
        messagesEndRef,
        highlightTarget,
        showWorkflowAnalysis, setShowWorkflowAnalysis,
        handleAction
    };
};
